import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import css from './LandingPage.module.css';
import { ListingCard } from '../../components';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  LANDING_FEATURED_LISTINGS_PARENT_PAGE,
  LANDING_FEATURED_LISTINGS_SECTION_ID,
  LANDING_CATEGORY_FEATURED_SECTION_IDS,
} from './LandingPage.duck';

const renterSearchPath = '/s';
const hostSignupPath = '/signup';
const hostListingPath = '/l/new';
const logoPath = '/static/logos/throttlex-logo.png';



const categories = [
  {
    title: 'Jet Skis',
    description: 'Fast lake day rentals',
    href: '/s?keywords=jet%20ski',
    imageClass: 'jetSki',
  },
  {
    title: 'Boats',
    description: 'Family and group water trips',
    href: '/s?keywords=boat',
    imageClass: 'boat',
  },
  {
    title: 'UTVs',
    description: 'Dunes and off-road escapes',
    href: '/s?keywords=utv',
    imageClass: 'utv',
  },
];

const vehicleTypes = [
  {
    title: 'Jet Skis',
    eyebrow: 'Water',
    description:
      'Great for lake days, river trips, quick rides, and high-energy weekends on the water.',
    href: '/s?keywords=jet%20ski',
    imageClass: 'vehicleJetSki',
    bullets: ['Best for 1–2 riders', 'Half-day or full-day bookings', 'Ideal for lakes and marinas'],
  },
  {
    title: 'Boats',
    eyebrow: 'Group trips',
    description: 'Perfect for families, cruising, sightseeing, fishing, and relaxed summer water days.',
    href: '/s?keywords=boat',
    imageClass: 'vehicleBoat',
    bullets: ['Best for groups and families', 'Great for full-day outings', 'Works well with add-ons'],
  },
  {
    title: 'UTVs',
    eyebrow: 'Off-road',
    description: 'Built for dunes, desert trips, trails, and group off-road adventures.',
    href: '/s?keywords=utv',
    imageClass: 'vehicleUtv',
    bullets: ['Best for 2–4+ riders', 'Popular for desert weekends', 'Great for groups'],
  },
];

const confidenceItems = [
  {
    number: '01',
    title: 'Confirmed bookings only',
    description: 'Keep bookings, payments, changes, and trip communication on ThrottleX so the rental is properly documented.',
  },
  {
    number: '02',
    title: 'Verify renter requirements',
    description: 'Review age limits, license or certification needs, safety gear, operator rules, and insurance requirements before pickup.',
  },
  {
    number: '03',
    title: 'Document pickup and return',
    description: 'Take clear photos or videos before and after the rental. Report damage, missing equipment, or late returns quickly.',
  },
  {
    number: '04',
    title: 'Ride safely',
    description: 'Follow all posted laws, owner instructions, safety rules, waterway rules, trail rules, and equipment-specific requirements.',
  },
];




const howItWorksSteps = [
  {
    number: '01',
    title: 'Search your adventure',
    description: 'Browse by rental type, location, or trip style to find the right vehicle.',
  },
  {
    number: '02',
    title: 'Choose your rental',
    description: 'Compare listings, view details, check pricing, and select the best fit.',
  },
  {
    number: '03',
    title: 'Book and ride',
    description: 'Reserve your rental and get ready for the lake, dunes, trails, or road trip.',
  },
];


const popularDestinations = [
  {
    title: 'Lake Havasu City',
    classSlug: 'LakeHavasu',
    region: 'Arizona',
    stateLabel: 'Arizona',
    description:
      'A high-energy lake destination known for jet skis, boats, sandbars, river access, and big summer weekends.',
    knownFor: 'Jet skis, boats, sandbars, and river runs',
    bestFor: 'Lake day rentals',
    href: '/s?address=Lake%20Havasu%20City%2C%20AZ',
  },
  {
    title: 'Las Vegas',
    classSlug: 'LasVegas',
    region: 'Nevada',
    stateLabel: 'Nevada',
    description:
      'A major adventure launch point with easy access to off-road terrain, nearby water destinations, and group rental trips.',
    knownFor: 'UTVs, nearby lake access, and weekend adventure rentals',
    bestFor: 'Desert + lake access',
    href: '/s?address=Las%20Vegas%2C%20NV',
  },
  {
    title: 'Sand Hollow',
    classSlug: 'SandHollow',
    region: 'Utah',
    stateLabel: 'Utah',
    description:
      'A top off-road and water destination known for red sand dunes, trail riding, lake access, and all-out weekend sessions.',
    knownFor: 'UTVs, red sand, lake access, and off-road weekends',
    bestFor: 'UTV destination',
    href: '/s?address=Sand%20Hollow%2C%20UT',
  },
  {
    title: 'Lake Powell',
    classSlug: 'LakePowell',
    region: 'Arizona / Utah',
    stateLabel: 'Arizona / Utah',
    description:
      'A scenic water destination built for boats, group trips, long lake days, and iconic canyon-backed outdoor getaways.',
    knownFor: 'Boats, group rentals, scenic lake trips, and houseboat culture',
    bestFor: 'Boat destination',
    href: '/s?address=Lake%20Powell',
  },
  {
    title: 'Laughlin',
    classSlug: 'Laughlin',
    region: 'Nevada',
    stateLabel: 'Nevada',
    description:
      'A river-focused destination with quick access to jet skis, boats, desert routes, and warm-weather water weekends.',
    knownFor: 'River rentals, jet skis, boats, and desert access',
    bestFor: 'River rentals',
    href: '/s?address=Laughlin%2C%20NV',
  },
];

const locationLinks = [
  {
    label: 'Sand Hollow',
    href:
      '/s?address=Sand%20Hollow%2C%20UT&bounds=37.28%2C-113.10%2C36.96%2C-113.62',
  },
  {
    label: 'Lake Havasu',
    href:
      '/s?address=Lake%20Havasu%20City%2C%20AZ&bounds=34.75%2C-114.05%2C34.20%2C-114.60',
  },
  {
    label: 'Laughlin',
    href:
      '/s?address=Laughlin%2C%20NV&bounds=35.38%2C-114.35%2C34.95%2C-114.80',
  },
  {
    label: 'Las Vegas',
    href:
      '/s?address=Las%20Vegas%2C%20NV&bounds=36.42%2C-114.90%2C35.92%2C-115.40',
  },
];

const trustItems = [
  {
    title: 'Trusted hosts',
    description: 'Professional listings with clear details and stronger presentation.',
  },
  {
    title: 'Simple booking',
    description: 'Search, compare, and reserve your next outdoor rental quickly.',
  },
  {
    title: 'Adventure focused',
    description: 'Built specifically for water, trail, desert, and weekend rentals.',
  },
];

const ownerListingRequirements = [
  {
    number: '01',
    title: 'Vehicle details',
    description:
      'Owners should list the year, make, model, vehicle type, seating capacity, fuel type, and any important specs.',
  },
  {
    number: '02',
    title: 'Clear photos',
    description:
      'Each listing should include clean exterior photos, detail shots, damage areas, trailer photos, and included gear.',
  },
  {
    number: '03',
    title: 'Pricing and deposits',
    description:
      'Owners should clearly show daily pricing, hourly pricing if offered, deposits, late fees, delivery fees, and add-ons.',
  },
  {
    number: '04',
    title: 'Rules and requirements',
    description:
      'Listings should include age limits, license needs, safety rules, experience level, pickup rules, and prohibited use.',
  },
];

const ownerChecklistItems = [
    'Current registration information',
  'Insurance or rental coverage details',
  'Clean photos of the vehicle',
  'Pickup and drop-off instructions',
  'Daily or hourly rental pricing',
  'Deposit and damage policy',
  'Safety gear and included equipment',
];

const rentableOwnerTypes = [
  {
    title: 'Water rentals',
    description: 'Jet skis, boats, pontoons, fishing boats, and lake-day equipment.',
  },
  {
    title: 'Off-road rentals',
    description: 'UTVs, side-by-sides, and desert-ready vehicles.',
  },
  {
    title: 'Trip add-ons',
    description: 'Trailers, coolers, helmets, life jackets, delivery, and other extras.',
  },
];

const ownerFlowItems = [
  {
    number: '01',
    title: 'Create your host profile',
    description: 'Add basic contact details, profile information, and the type of rentals you offer.',
  },
  {
    number: '02',
    title: 'Add vehicle details',
    description: 'Upload photos, descriptions, pricing, pickup notes, rules, and availability.',
  },
  {
    number: '03',
    title: 'Publish and receive requests',
    description: 'Once approved, renters can discover your listing and start booking adventures.',
  },
];

const ownerPreparationItems = [
  'Clear vehicle photos',
  'Daily or hourly rental pricing',
  'Pickup/drop-off instructions',
  'Safety rules and requirements',
  'Availability and blackout dates',
];

const ownerBenefits = [
  {
    title: 'Showcase every detail',
    description: 'Add photos, descriptions, pricing, availability, pickup notes, and renter details.',
  },
  {
    title: 'Reach adventure renters',
    description: 'Present your rentals to customers actively searching for outdoor experiences.',
  },
  {
    title: 'Build a real rental brand',
    description: 'Create a marketplace experience that feels premium instead of generic.',
  },
];


const faqItems = [
  {
    question: 'What types of rentals can I find on ThrottleX?',
    answer:
      'ThrottleX helps renters find outdoor adventure rentals like jet skis, boats, UTVs, side-by-sides, and similar recreational vehicles from local hosts.',
  },
  {
    question: 'Can I rent more than one vehicle for a trip?',
    answer:
      'Yes. Renters can browse multiple listings and coordinate the right setup for lake days, desert weekends, river trips, and group adventures.',
  },
  {
    question: 'How do I know what is included with a rental?',
    answer:
      'Each listing should include rental details such as included equipment, pickup instructions, renter requirements, pricing, deposit information, and any owner-specific rules.',
  },
  {
    question: 'Are security deposits required?',
    answer:
      'When a security deposit applies, the amount and related damage terms are shown before booking so renters know what is required before confirming the rental.',
  },
  {
    question: 'Can owners list different types of vehicles?',
    answer:
      'Yes. Owners can list eligible adventure rentals such as jet skis, boats, UTVs, side-by-sides, and other approved outdoor rental vehicles.',
  },
  {
    question: 'How do pickup and return details work?',
    answer:
      'Pickup, drop-off, timing, return expectations, and special instructions are provided through the listing details and booking communication between the renter and owner.',
  },
];

export
const homeCountySearchCards = [
  {
    name: 'Los Angeles County',
    rentals: 'Boats + Jet Skis',
    description: 'Lake days, harbor trips, and group water rentals.',
    href: '/s?address=Los%20Angeles%20County%2C%20CA&bounds=34.82%2C-117.65%2C33.70%2C-118.95',
  },
  {
    name: 'Orange County',
    rentals: 'Boats + Jet Skis',
    description: 'Coastal trips, harbor rentals, and beach-area weekends.',
    href: '/s?address=Orange%20County%2C%20CA&bounds=33.95%2C-117.41%2C33.33%2C-118.12',
  },
  {
    name: 'Riverside County',
    rentals: 'Boats + Jet Skis + UTVs',
    description: 'Lake days, inland trips, and desert weekend rentals.',
    href: '/s?address=Riverside%20County%2C%20CA&bounds=34.08%2C-114.43%2C33.42%2C-117.68',
  },
  {
    name: 'San Bernardino County',
    rentals: 'UTVs + Adventure Rentals',
    description: 'High-desert riding, off-road trips, and weekend adventures.',
    href: '/s?address=San%20Bernardino%20County%2C%20CA&bounds=35.80%2C-114.13%2C33.86%2C-117.80',
  },
  {
    name: 'San Diego County',
    rentals: 'Boats + Jet Skis + UTVs',
    description: 'Coastal water rentals plus desert-access UTV trips.',
    href: '/s?address=San%20Diego%20County%2C%20CA&bounds=33.52%2C-116.08%2C32.53%2C-117.60',
  },
  {
    name: 'Imperial County',
    rentals: 'UTVs + Side-by-sides',
    description: 'Dunes, desert camps, and Glamis-style off-road weekends.',
    href: '/s?address=Imperial%20County%2C%20CA&bounds=33.44%2C-114.72%2C32.53%2C-116.12',
  },
];

const HomeCountySearchSection = () => (
  <section className={css.homeCountySearchSection}>
    <div className={css.homeCountySearchIntro}>
      <p>Search by county</p>
      <h2>Find rentals near your trip area</h2>
      <span>
        Browse Southern California rentals by county and jump straight into local listings
        for boats, jet skis, and UTVs.
      </span>
    </div>

    <div className={css.homeCountySearchGrid}>
      {homeCountySearchCards.map(county => (
        <a className={css.homeCountySearchCard} href={county.href} key={county.name}>
          <div>
            <span>{county.rentals}</span>
            <h3>{county.name}</h3>
            <p>{county.description}</p>
          </div>
          <strong>View listings</strong>
        </a>
      ))}
    </div>

    <a className={css.homeCountyMapLink} href="/p/socal-rentals">
      View full county map
    </a>
  </section>
);


const LandingPageComponent = props => {
  const {
    liveListings = [],
    liveListingsInProgress = false,
    liveListingsFetched = false,
    categoryFeaturedListings = [],
    categoryFeaturedInProgress = false,
  } = props;
  const [keywords, setKeywords] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const handleSearch = () => {
    const keywordParam = keywords ? `keywords=${encodeURIComponent(keywords)}` : '';
    window.location.href = keywordParam ? `/s?${keywordParam}` : renterSearchPath;
  };

  const handleEnterKey = e => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const renderLogo = isFooter => {
    return (
      <span className={isFooter ? css.footerLogoWrap : css.logoWrap}>
        {!logoFailed ? (
          <img
            src={logoPath}
            alt="ThrottleX"
            className={isFooter ? css.footerLogoImage : css.logoImage}
            onError={() => setLogoFailed(true)}
          />
        ) : null}

        {logoFailed ? (
          <span className={isFooter ? css.footerLogoFallback : css.logoFallback}>
            <span>Throttle</span>
            <span>X</span>
          </span>
        ) : null}
      </span>
    );
  };
    return (
  <div className={css.page}>
    <LandingHeader />

    <section className={css.hero}>
        <div className={css.heroGlowBlue} />
        <div className={css.heroGlowOrange} />

        <div className={css.heroInner}>
          <div className={css.heroContent}>
            <p className={css.eyebrow}>Rent the rush</p>

            <h1 className={css.title}>Adventure rentals with a faster, premium feel</h1>

            <p className={css.subtitle}>
              Browse Jet Skis, Boats, and UTVs from local hosts. Built for lake days, desert
              weekends, trail rides, and high-energy outdoor getaways.
            </p>

            <div className={css.searchBox}>
              <div className={css.field}>
                <label>What are you looking for?</label>
                <input
                  type="text"
                  placeholder="Search jet ski, UTV, boat..."
                  value={keywords}
                  onChange={e => setKeywords(e.target.value)}
                  onKeyDown={handleEnterKey}
                />
              </div>

              <button type="button" onClick={handleSearch}>
                Search
              </button>
            </div>

            <div className={css.quickLinks}>
              {categories.map(category => (
                <a key={category.title} href={category.href}>
                  {category.title}
                </a>
              ))}
            </div>

            <div className={css.actions}>
              <a href={renterSearchPath}>Browse rentals</a>
              <a href="#hosting">List your vehicle</a>
            </div>

            <div className={css.heroMetrics}>
              <div className={css.metricCard}>
                <span>Categories</span>
                <strong>3</strong>
              </div>
              <div className={css.metricCard}>
                <span>Built for</span>
                <strong>Water + Off-road</strong>
              </div>
              <div className={css.metricCard}>
                <span>Brand vibe</span>
                <strong>Fast / premium</strong>
              </div>
            </div>
          </div>

          <div className={css.heroShowcase}>
            <div className={css.showcaseMain}>
              <div className={css.showcaseBadge}>ThrottleX rental marketplace</div>
              <h3>Premium outdoor rentals</h3>
              <p>
                Browse jet skis, boats, and UTVs from local hosts for lake days, desert weekends, river trips, and off-road getaways.
              </p>

              <div className={css.showcaseBars}>
                <div>
                  <span>Jet Skis</span>
                  <strong>High demand</strong>
                </div>
                <div>
                  <span>UTVs</span>
                  <strong>Weekend ready</strong>
                </div>
                <div>
                  <span>Boats</span>
                  <strong>Group trips</strong>
                </div>
              </div>
            </div>

            <div className={css.showcaseMiniGrid}>
              <div className={css.showcaseMiniCard}>
                <span>Lake</span>
                <strong>Jet Ski rentals</strong>
              </div>
              <div className={css.showcaseMiniCard}>
                <span>Desert</span>
                <strong>UTV adventures</strong>
              </div>
              <div className={css.showcaseMiniCard}>
                <span>Group</span>
                <strong>Boat trips</strong>
              </div>
              <div className={css.showcaseMiniCard}>
                <span>Premium</span>
                <strong>Adventure rentals</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THROTTLEX_FREE_SIGNUP_START */}
      <section className={css.freeSignupSection}>
        <div className={css.freeSignupGlow} />

        <div className={css.freeSignupInner}>
          <div className={css.freeSignupContent}>
            <p className={css.freeSignupEyebrow}>Free to get started</p>

            <h2>Own the ride? List it for free.</h2>

            <p className={css.freeSignupIntro}>
              Create your ThrottleX account and list your Jet Ski, boat, or UTV with
              no upfront listing fee. ThrottleX earns when you earn.
            </p>

            <div className={css.freeSignupBenefits}>
              <div>
                <strong>$0</strong>
                <span>to sign up</span>
              </div>

              <div>
                <strong>$0</strong>
                <span>to list</span>
              </div>

              <div>
                <strong>10%</strong>
                <span>owner service fee when booked</span>
              </div>
            </div>

            <div className={css.freeSignupActions}>
              <a href="/signup" className={css.freeSignupPrimary}>
                Start hosting free
              </a>

              <a href="/info/listing-requirements" className={css.freeSignupSecondary}>
                View listing requirements
              </a>
            </div>
          </div>

          <div className={css.freeSignupSide}>
            <span className={css.freeSignupSideLabel}>THROTTLEX OWNERS</span>

            <div className={css.freeSignupZero}>
              <span>$</span>
              <strong>0</strong>
            </div>

            <p>upfront cost to get your rental listed</p>
          </div>
        </div>
      </section>
      {/* THROTTLEX_FREE_SIGNUP_END */}



      <HomeCountySearchSection />

      <section className={css.destinationMapSection} id="popular-destinations">
        <div className={css.destinationMapHeader}>
          <p className={css.eyebrowDark}>Popular destinations</p>
          <h2>Explore rentals by adventure destination</h2>
          <p>
            Pick a destination to see where it sits in the region and what it is known for,
            then jump straight into nearby rentals.
          </p>
        </div>

        <div className={css.destinationMapShell}>
          <div className={css.destinationMapVisual} aria-label="Popular ThrottleX destination map">
            <div className={`${css.destinationStateCard} ${css.destinationStateLakeHavasu}`}>
              <div className={css.destinationStateBadge}>Destination map</div>
              <div className={css.destinationStateCanvas}>
                <div className={css.destinationStateSvgWrap}>
                  <svg viewBox="0 0 320 320" className={css.destinationStateSvg} aria-hidden="true" focusable="false">
                    <path
                      className={css.destinationStateShape}
                      d="M75 32 H260 V232 L238 255 L229 287 L207 251 L170 241 L145 216 L126 181 L104 151 L86 115 L75 94 Z"
                    />
                  </svg>
                  <div className={css.destinationStatePin} style={{ left: '39%', top: '65%' }}>
                    <span />
                  </div>
                </div>
              </div>
              <div className={css.destinationStateMeta}>
                <span>Arizona</span>
                <strong>Lake Havasu City</strong>
              </div>
            </div>

            <div className={`${css.destinationStateCard} ${css.destinationStateLasVegas}`}>
              <div className={css.destinationStateBadge}>Destination map</div>
              <div className={css.destinationStateCanvas}>
                <div className={css.destinationStateSvgWrap}>
                  <svg viewBox="0 0 320 320" className={css.destinationStateSvg} aria-hidden="true" focusable="false">
                    <path
                      className={css.destinationStateShape}
                      d="M118 28 H258 V242 L238 272 L238 294 L72 126 L72 84 Z"
                    />
                  </svg>
                  <div className={css.destinationStatePin} style={{ left: '70%', top: '72%' }}>
                    <span />
                  </div>
                </div>
              </div>
              <div className={css.destinationStateMeta}>
                <span>Nevada</span>
                <strong>Las Vegas</strong>
              </div>
            </div>

            <div className={`${css.destinationStateCard} ${css.destinationStateSandHollow}`}>
              <div className={css.destinationStateBadge}>Destination map</div>
              <div className={css.destinationStateCanvas}>
                <div className={css.destinationStateSvgWrap}>
                  <svg viewBox="0 0 320 320" className={css.destinationStateSvg} aria-hidden="true" focusable="false">
                    <path
                      className={css.destinationStateShape}
                      d="M82 30 H248 V286 H110 V250 L82 236 Z"
                    />
                  </svg>
                  <div className={css.destinationStatePin} style={{ left: '37%', top: '80%' }}>
                    <span />
                  </div>
                </div>
              </div>
              <div className={css.destinationStateMeta}>
                <span>Utah</span>
                <strong>Sand Hollow</strong>
              </div>
            </div>

            <div className={`${css.destinationStateCard} ${css.destinationStateLakePowell}`}>
              <div className={css.destinationStateBadge}>Destination map</div>
              <div className={css.destinationStateDualCanvas}>
                <div className={css.destinationStateMini}>
                  <div className={css.destinationStateSvgWrapSmall}>
                    <svg viewBox="0 0 320 320" className={css.destinationStateSvg} aria-hidden="true" focusable="false">
                      <path
                        className={css.destinationStateShape}
                        d="M82 30 H248 V286 H110 V250 L82 236 Z"
                      />
                    </svg>
                    <div className={css.destinationStatePin} style={{ left: '80%', top: '58%' }}>
                      <span />
                    </div>
                  </div>
                  <small>Utah</small>
                </div>

                <div className={css.destinationStateMini}>
                  <div className={css.destinationStateSvgWrapSmall}>
                    <svg viewBox="0 0 320 320" className={css.destinationStateSvg} aria-hidden="true" focusable="false">
                      <path
                        className={css.destinationStateShape}
                        d="M75 32 H260 V232 L238 255 L229 287 L207 251 L170 241 L145 216 L126 181 L104 151 L86 115 L75 94 Z"
                      />
                    </svg>
                    <div className={css.destinationStatePin} style={{ left: '58%', top: '30%' }}>
                      <span />
                    </div>
                  </div>
                  <small>Arizona</small>
                </div>
              </div>
              <div className={css.destinationStateMeta}>
                <span>Arizona / Utah</span>
                <strong>Lake Powell</strong>
              </div>
            </div>

            <div className={`${css.destinationStateCard} ${css.destinationStateLaughlin}`}>
              <div className={css.destinationStateBadge}>Destination map</div>
              <div className={css.destinationStateCanvas}>
                <div className={css.destinationStateSvgWrap}>
                  <svg viewBox="0 0 320 320" className={css.destinationStateSvg} aria-hidden="true" focusable="false">
                    <path
                      className={css.destinationStateShape}
                      d="M118 28 H258 V242 L238 272 L238 294 L72 126 L72 84 Z"
                    />
                  </svg>
                  <div className={css.destinationStatePin} style={{ left: '74%', top: '86%' }}>
                    <span />
                  </div>
                </div>
              </div>
              <div className={css.destinationStateMeta}>
                <span>Nevada</span>
                <strong>Laughlin</strong>
              </div>
            </div>
          </div>

          <div className={css.destinationAccordion}>
            {popularDestinations.map((destination, index) => (
              <details
                key={destination.title}
                name="popular-destinations"
                className={`${css.destinationAccordionItem} ${css[`destinationItem${destination.classSlug}`]}`}
                open={index === 0}
              >
                <summary className={css.destinationAccordionButton}>
                  <span>
                    <small>{destination.region}</small>
                    {destination.title}
                  </span>
                  <strong>+</strong>
                </summary>

                <div className={css.destinationDropdown}>
                  <p>{destination.description}</p>

                  <div className={css.destinationDetailsGrid}>
                    <div>
                      <span>Known for</span>
                      <strong>{destination.knownFor}</strong>
                    </div>
                    <div>
                      <span>Best for</span>
                      <strong>{destination.bestFor}</strong>
                    </div>
                  </div>

                  <a href={destination.href}>Search rentals in {destination.title}</a>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>









<section className={css.section} id="categories">
        <div className={css.sectionHeader}>
          <p className={css.eyebrowDark}>Browse faster</p>
          <h2>Explore by category</h2>
          <p>
            Find the right rental for your trip, whether you are headed to the lake, dunes, trails,
            or a weekend getaway.
          </p>
        </div>

        <div className={css.grid}>
          {categories.map(category => (
            <a key={category.title} href={category.href} className={css.card}>
              <div className={`${css.cardImage} ${css[category.imageClass]}`} />
              <div className={css.cardAccent} />
              <h3>{category.title}</h3>
              <p>{category.description}</p>
            </a>
          ))}
        </div>
      </section>



      <section className={css.renterConfidenceSection}>
        <div className={css.confidencePanel}>
          <div className={css.confidenceIntro}>
            <p className={css.eyebrow}>Book with more confidence</p>
            <h2>Know what to check before you ride</h2>
            <p>
              Know what to check before you rent. Review the listing details, renter requirements, deposit amount, safety rules, pickup process, and return expectations before confirming your trip.
            </p>
          </div>

          <div className={css.confidenceGrid}>
            {confidenceItems.map(item => (
              <div key={item.number} className={css.confidenceCard}>
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {liveListings.length > 0 ? (
      <section className={css.marketplacePreviewSection} id="marketplace-preview">
        <div className={css.marketplaceHeader}>
          <div>
            <p className={css.eyebrowDark}>Live marketplace</p>
            <h2>Available rentals</h2>
            <p>
              Explore real rentals listed by ThrottleX hosts. Choose your ride, review the
              details, and start planning your next lake, desert, or weekend trip.
            </p>
          </div>

          <a href={renterSearchPath} className={css.viewAllLink}>
            Browse all
          </a>
        </div>

        <div className={css.previewGrid}>
          {liveListingsInProgress && !liveListings.length ? (
            <div className={css.liveListingsEmpty}>
              <strong>Loading current rentals...</strong>
              <p>We are pulling the latest published listings from the marketplace.</p>
            </div>
          ) : liveListings.length > 0 ? (
            liveListings.slice(0, 6).map(listing => (
              <ListingCard
                key={listing.id.uuid}
                rootClassName={css.liveListingCard}
                aspectRatioClassName={css.liveListingImage}
                listing={listing}
                renderSizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw"
                showAuthorInfo={false}
                darkMode
                lazyLoadImage={false}
              />
            ))
          ) : (
            <div className={css.liveListingsEmpty}>
              <strong>No published rentals yet</strong>
              <p>
                Once listings are published in Sharetribe, they will show here automatically.
              </p>
              <a href={hostListingPath}>List your vehicle</a>
              <p className={css.hostFreeNote}>Free to list · $0 to get started</p>
            </div>
          )}
        </div>
      </section>
      ) : null}

      <section className={css.featuredSection}>
        <div className={css.featuredHeader}>
          <div>
            <p className={css.eyebrowDark}>Top picks</p>
            <h2>Featured rentals by category</h2>
          </div>

          <a href={renterSearchPath} className={css.viewAllLink}>
            View all rentals
          </a>
        </div>

        <div className={css.categoryFeaturedGrid}>
          {categoryFeaturedInProgress && categoryFeaturedListings.length === 0 ? (
            <div className={css.categoryFeaturedEmpty}>
              <strong>Loading featured rentals...</strong>
              <p>We are pulling one live listing from each rental category.</p>
            </div>
          ) : categoryFeaturedListings.length > 0 ? (
            categoryFeaturedListings.map(item => (
              <div key={item.key} className={css.categoryFeaturedSlot}>
                <div className={css.categoryFeaturedLabel}>{item.label}</div>
                <ListingCard
                  rootClassName={css.categoryFeaturedCard}
                  aspectRatioClassName={css.categoryFeaturedImage}
                  listing={item.listing}
                  renderSizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw"
                  showAuthorInfo={false}
                  darkMode
                  lazyLoadImage={false}
                />
              </div>
            ))
          ) : (
            <div className={css.categoryFeaturedEmpty}>
              <strong>Featured rentals</strong>
              <p>
                </p>
              <a href={hostListingPath}>List your vehicle</a>
            </div>
          )}
        </div>
      </section>

      <section className={css.howItWorksSection} id="how-it-works">
        <div className={css.sectionHeaderCentered}>
          <p className={css.eyebrowDark}>Simple process</p>
          <h2>How it works</h2>
          <p>Built to make outdoor rentals feel simple, premium, and easy to book.</p>
        </div>

        <div className={css.stepsGrid}>
          {howItWorksSteps.map(step => (
            <div key={step.number} className={css.stepCard}>
              <div className={css.stepNumber}>{step.number}</div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={css.locationsSection}>
        <div className={css.locationsContent}>
          <p className={css.eyebrowDark}>Popular destinations</p>
          <h2>Find rentals near top riding destinations</h2>
          <p>
            Find rentals near high-demand outdoor destinations, from lakes and rivers to desert riding spots and weekend getaway areas.
          </p>

          <div className={css.locationChips}>
            {locationLinks.map(location => (
              <a key={location.label} href={location.href}>
                {location.label}
              </a>
            ))}
          </div>
        </div>

        <div className={css.locationsImage} />
      </section>

      <section className={css.darkSection}>
        <div className={css.darkHeader}>
          <p className={css.eyebrow}>Why book here</p>
          <h2>A cleaner, easier rental experience</h2>
        </div>

        <div className={css.trustGrid}>
          {trustItems.map(item => (
            <div key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={css.hostSection} id="hosting">
        <div className={css.hostCard}>
          <div className={css.hostText}>
            <p className={css.eyebrowDark}>For owners</p>
            <h2>Turn your vehicles into income</h2>
            <p>
              List your jet ski, boat, or UTV and reach renters looking for their next adventure.
            </p>

            <div className={css.hostButtonRow}>
              <a href={hostSignupPath}>Create host account</a>
              <a href={hostListingPath}>Add a listing</a>
            </div>

            <p className={css.hostNote}>
              New owners should create an account first. Existing owners can go straight to adding a
              rental listing.
            </p>
          </div>

          <div className={css.hostImage} />
        </div>
      </section>

      <section className={css.ownerRequirementsSection}>
        <div className={css.ownerRequirementsHeader}>
          <p className={css.eyebrowDark}>Owner listing requirements</p>
          <h2>Help owners know exactly what they need to list</h2>
          <p>
            This section helps vehicle owners understand what they need before listing their jet ski, boat, or UTV on ThrottleX.
          </p>
        </div>

        <div className={css.ownerRequirementsShell}>
          <div className={css.ownerRequirementCards}>
            {ownerListingRequirements.map(item => (
              <div key={item.number} className={css.ownerRequirementCard}>
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>

          <div className={css.ownerChecklistPanel}>
            <p className={css.eyebrow}>Owner checklist</p>
            <h3>What to prepare before listing</h3>

            <ul>
              {ownerChecklistItems.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>

            <a href={hostListingPath}>Start a listing</a>
          </div>
        </div>

        <div className={css.rentableTypesGrid}>
          {rentableOwnerTypes.map(item => (
            <div key={item.title} className={css.rentableTypeCard}>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={css.hostPathSection}>
        <div className={css.hostPathHeader}>
          <p className={css.eyebrowDark}>Host path</p>
          <h2>Choose the right next step</h2>
          <p>
            This keeps the homepage clean now, while staying easy to connect to your real host flow
            later.
          </p>
        </div>

        <div className={css.hostPathGrid}>
          <a href={hostSignupPath} className={css.hostPathCard}>
            <span>New owner</span>
            <h3>Create your host account</h3>
            <p>
              Start here if you are new. Set up your profile first, then add vehicle details when
              ready.
            </p>
            <strong>Start hosting</strong>
          </a>

          <a href={hostListingPath} className={css.hostPathCardDark}>
            <span>Existing owner</span>
            <h3>Add your rental listing</h3>
            <p>
              Already signed in? Add your jet ski, boat, UTV, or other adventure rental to the
              marketplace.
            </p>
            <strong>Add listing</strong>
          </a>
        </div>
      </section>



      

      <section className={css.faqSection}>
        <div className={css.sectionHeader}>
          <p className={css.eyebrowDark}>Questions</p>
          <h2>Frequently asked questions</h2>
        </div>

        <div className={css.faqGrid}>
          {faqItems.map(item => (
            <div key={item.question} className={css.faqItem}>
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={css.finalCta}>
        <div>
          <p className={css.eyebrow}>Ready for your next adventure?</p>
          <h2>Browse rentals or start earning as a host.</h2>
        </div>

        <div className={css.finalActions}>
          <a href={renterSearchPath}>Browse rentals</a>
          <a href={hostSignupPath}>Start hosting</a>
        </div>
      </section>
<footer className={css.footer}>
        <div className={css.footerBrand}>
          <a href="/" className={css.footerLogo}>
            {renderLogo(true)}
          </a>
          <p>Premium outdoor rentals for lakes, dunes, trails, and weekend adventures.</p>
        </div>

        <div className={css.footerColumns}>
          <div className={css.footerColumn}>
            <h3>Marketplace</h3>
            <a href={renterSearchPath}>Browse rentals</a>
            <a href="#categories">Categories</a>
            <a href="#marketplace-preview">Featured listings</a>
            <a href="#how-it-works">How it works</a>
          </div>

          <div className={css.footerColumn}>
            <h3>Owners</h3>
            <a href={hostSignupPath}>Start hosting</a>
            <a href={hostListingPath}>Add a listing</a>
            <a href="#hosting">Owner benefits</a>
            <a href="#hosting">Listing requirements</a>
          </div>

          <div className={css.footerColumn}>
            <h3>Support</h3>
            <a href="/p/safety-guidelines">Safety guidelines</a>
            <a href="/p/rental-rules">Rental rules</a>
            <a href="/p/insurance">Insurance</a>
            <a href="/p/contact">Contact</a>
          </div>

          <div className={css.footerColumn}>
            <h3>Legal</h3>
            <a href="/p/terms">Terms</a>
            <a href="/p/privacy">Privacy</a>
            <a href="/p/host-standards">Host standards</a>
                  <a href="/p/faq">FAQ</a>
            <a href="/p/socal-rentals">Search by county</a>
            <a href="/p/utv-renter-safety-checklist">UTV renter checklist</a>
            <a href="/login">Log in</a>
          </div>
        </div>

        <div className={css.footerBottom}>
          <p>© {new Date().getFullYear()} ThrottleX. All rights reserved.</p>

          <div>
            <a href="/p/terms">Terms</a>
            <a href="/p/privacy">Privacy</a>
            <a href="/p/contact">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};
import LandingHeader from './LandingHeader';
const mapStateToProps = state => {
  const featuredStateByPage =
    state.featuredListings?.[LANDING_FEATURED_LISTINGS_PARENT_PAGE] || {};

  const liveState = featuredStateByPage[LANDING_FEATURED_LISTINGS_SECTION_ID] || {};
  const liveListingIds = liveState.listingIds || [];
  const liveListingRefs = liveListingIds.map(id => ({ type: 'listing', id }));
  const liveListings = getMarketplaceEntities(state, liveListingRefs);

  const getFirstListingForSection = sectionId => {
    const sectionState = featuredStateByPage[sectionId] || {};
    const firstListingId = sectionState.listingIds?.[0];

    return firstListingId
      ? getMarketplaceEntities(state, [{ type: 'listing', id: firstListingId }])[0]
      : null;
  };

  const categoryFeaturedListings = [
    {
      key: 'jetSki',
      label: 'Jet Skis',
      listing: getFirstListingForSection(LANDING_CATEGORY_FEATURED_SECTION_IDS.jetSki),
    },
    {
      key: 'boat',
      label: 'Boats',
      listing: getFirstListingForSection(LANDING_CATEGORY_FEATURED_SECTION_IDS.boat),
    },
    {
      key: 'utv',
      label: 'UTVs',
      listing: getFirstListingForSection(LANDING_CATEGORY_FEATURED_SECTION_IDS.utv),
    },
  ].filter(item => item.listing);

  const categoryFeaturedInProgress = [
    LANDING_CATEGORY_FEATURED_SECTION_IDS.jetSki,
    LANDING_CATEGORY_FEATURED_SECTION_IDS.boat,
    LANDING_CATEGORY_FEATURED_SECTION_IDS.utv,
  ].some(sectionId => !!featuredStateByPage[sectionId]?.inProgress);

  return {
    liveListings,
    liveListingsInProgress: !!liveState.inProgress,
    liveListingsFetched: !!liveState.fetched,
    categoryFeaturedListings,
    categoryFeaturedInProgress,
  };
};

const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
